export type TweetEmbedPost = {
  id: string;
  url: string;
  text: string;
  /** 投稿日。日付の書式は環境で揺れるので、描画側では作らずサーバーで作った字を受け取る */
  dateLabel: string;
  author: {
    name: string;
    handle: string;
    avatarUrl: string | null;
  };
  mediaUrl: string | null;
};

/**
 * 記事の本文に並べる X の投稿。キーは投稿の ID。
 * 値が null なのは「投稿が消えた、または出せない」と分かったもの。
 * キーが無いのは取りに行けなかったもの。
 */
export type TweetEmbedMap = Record<string, TweetEmbedPost | null>;

/**
 * `::tweet[https://x.com/<名前>/status/<ID>]` のラベルか `{id="<ID>"}` から
 * 投稿の ID を取り出す。読み込みと描画で同じ規則を使うため、ここ1か所に置く
 */
export function tweetIdFromRef(ref: string | null | undefined): string | null {
  if (!ref) return null;
  const trimmed = ref.trim();
  if (/^\d{1,25}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(
    /^https?:\/\/(?:www\.|mobile\.)?(?:x|twitter)\.com\/[A-Za-z0-9_]{1,50}\/status\/(\d{1,25})(?:[/?#]\S*)?$/,
  );
  return match ? match[1] : null;
}

const TWEET_DIRECTIVE_LINE = /^::tweet(?:\[([^\]\n]*)\]|\{[^}\n]*?\bid="?(\d{1,25})"?[^}\n]*\})/gm;
const MAX_TWEETS_PER_ARTICLE = 10;

/** 本文に置かれた `::tweet` の投稿 ID を、出てくる順に重ならないよう拾う */
export function tweetIdsInMarkdown(markdown: string): string[] {
  const ids: string[] = [];
  // コードの例に書いた記法は埋め込みではない。先に取り除いてから拾う
  const prose = markdown.replace(/^(`{3,}|~{3,})[^\n]*\n[\s\S]*?^\1[^\n]*$/gm, "");
  for (const match of prose.matchAll(TWEET_DIRECTIVE_LINE)) {
    const id = tweetIdFromRef(match[1] ?? match[2]);
    if (id && !ids.includes(id)) ids.push(id);
    if (ids.length >= MAX_TWEETS_PER_ARTICLE) break;
  }
  return ids;
}

export function TweetEmbed({ post }: { post: TweetEmbedPost }) {
  return (
    <figure
      data-tweet-embed={post.id}
      className="my-8 rounded-[20px] border border-main bg-surface p-5"
    >
      <div className="flex items-center gap-3">
        {post.author.avatarUrl ? (
          <img
            src={post.author.avatarUrl}
            alt=""
            width={40}
            height={40}
            loading="lazy"
            decoding="async"
            className="h-10 w-10 shrink-0 rounded-full border border-main object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="truncate max-sm:text-[0.9375rem] text-sm font-black text-main">
            {post.author.name}
          </p>
          <p className="truncate max-sm:text-[0.9375rem] text-[13px] font-bold text-sub">
            @{post.author.handle}
          </p>
        </div>
        <span aria-hidden="true" className="text-lg font-black text-main">
          X
        </span>
      </div>
      <blockquote className="mt-4 whitespace-pre-wrap break-words text-[15px] leading-7 text-main">
        {post.text}
      </blockquote>
      {post.mediaUrl ? (
        <img
          src={post.mediaUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="mt-4 w-full rounded-xl border border-main object-cover"
        />
      ) : null}
      <figcaption className="mt-4 flex flex-wrap items-center justify-between gap-2 max-sm:text-[0.9375rem] text-[13px] text-sub">
        <span>{post.dateLabel}</span>
        <a
          href={post.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center font-bold text-primary underline-offset-4 hover:underline"
        >
          X で元の投稿を見る
        </a>
      </figcaption>
    </figure>
  );
}

/** 取りに行けなかった投稿は、カードの代わりに元の投稿へのリンクだけ出す */
export function TweetEmbedFallback({ url }: { url: string }) {
  return (
    <p className="my-6">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="font-bold text-primary underline underline-offset-4"
      >
        X の投稿を見る
      </a>
    </p>
  );
}
